/**
 * Exit waterfall, shared between the frontend and the Deno edge functions.
 *
 * This lives under supabase/functions/_shared so sm-exit-waterfall-sim and the UI
 * run the same code. Vitest, tsc and the Vite build all resolve it, so there is no
 * second copy to drift. Keep it free of Deno and browser APIs.
 */

export interface WaterfallRound {
  name: string;
  investmentAmount: number;
  liquidationPreference: number; // multiplier, e.g. 1, 2
  participating: boolean;
  participationCap: number | null;
  shares: number;
  /**
   * Where this round sits in the preference stack. **Lower is more senior**, so
   * rank 0 is paid before rank 1. Rounds sharing a rank are *pari passu*: they are
   * paid together, and if the proceeds cannot cover them all they share what there
   * is pro rata to their claims.
   *
   * Omitted, it defaults to chronological seniority — the last round in the array
   * is the most senior — which is what a stack with no negotiated ranking means
   * and what this function did before ranks existed.
   */
  seniorityRank?: number;
}

export interface WaterfallResult {
  roundName: string;
  liquidationPref: number;
  participating: number;
  common: number;
  total: number;
  roi: number;
  multiple: number;
}

/**
 * Distribution of exit proceeds across preferred rounds and common.
 *
 * `rounds` is in chronological order. By default later rounds are more senior and
 * are paid first; set `seniorityRank` to express a negotiated stack instead, where
 * rounds sharing a rank are pari passu and share a shortfall pro rata to their
 * claims. `participationCap` is a multiple of the investment and caps the round's
 * *total* return (preference + participation), which is how such caps are written.
 * Where a cap is set below the liquidation preference the two terms contradict
 * each other; the preference is senior and unconditional, so it is paid in full
 * and the cap simply leaves no room to participate.
 *
 * Two behaviours make this more than a single pass:
 *
 *  - **Any** round may waive its preference and take its as-converted pro-rata
 *    share instead, taking whichever pays more. Whether converting wins depends on
 *    the residual, which depends on who else converted — so the conversion set is
 *    solved by iterating to a fixed point rather than decided per-round in
 *    isolation.
 *  - When a participating round hits its cap, the excess returns to the pool and is
 *    redistributed among the remaining uncapped holders, which can push another
 *    round over its cap. That also iterates.
 *
 * The conversion election is **not** limited to non-participating rounds. Standard
 * terms give participating preferred — capped or not — an implicit right to waive
 * preference *and* participation and take common instead, and a cap exists
 * precisely to make that rational above some exit. Excluding participating rounds
 * from the solver, as this previously did, silently underpaid them: $1M at a 1x
 * preference, participating, capped at 1.1x, holding 10M shares against 1M common,
 * took $1.1M of a $50M exit while holding ~91% of the company, when converting
 * pays $45.45M. Because a converted round holds common, it is no longer subject to
 * its participation cap — that is the point of converting.
 *
 * Invariant: the sum of every payout equals `exitValue`.
 */
export function calcExitWaterfall(
  rounds: WaterfallRound[],
  commonShares: number,
  exitValue: number
): { results: WaterfallResult[]; commonPayout: number } {
  const EPSILON = 1e-9;
  const n = rounds.length;
  const proceeds = Math.max(0, exitValue);
  // Negative share counts are not meaningful and would make `activeShares <= 0`
  // mean something other than "nobody is eligible".
  const commonHeld = Math.max(0, commonShares);

  interface Distribution {
    pref: number[];
    participation: number[];
    common: number[];
    commonPayout: number;
  }

  // The stack, grouped into tiers and ordered most senior first. Rounds sharing a
  // rank form one tier and are paid together. This does not depend on who
  // converts, so it is computed once.
  const rankOf = (i: number) => {
    const declared = rounds[i].seniorityRank;
    return typeof declared === 'number' && Number.isFinite(declared) ? declared : n - 1 - i;
  };
  const tiers: number[][] = [...new Set(rounds.map((_, i) => rankOf(i)))]
    .sort((a, b) => a - b)
    .map((rank) => rounds.map((_, i) => i).filter((i) => rankOf(i) === rank));

  // Payouts for a given set of non-participating rounds electing to convert.
  const distribute = (converted: boolean[]): Distribution => {
    const pref = new Array<number>(n).fill(0);
    const participation = new Array<number>(n).fill(0);
    const common = new Array<number>(n).fill(0);
    let remaining = proceeds;

    // Preferences, most senior tier first. A converting round waives its
    // preference, which leaves that money in the pool rather than paying it out.
    //
    // Within a tier the rounds are pari passu: if the remaining proceeds cannot
    // cover every claim at that rank, they share what is left **pro rata to their
    // claims**, rather than one being paid in full ahead of the other. Where every
    // rank is distinct each tier holds a single round, the claim is either covered
    // or not, and this reduces exactly to paying them in order.
    for (const tier of tiers) {
      const claims = tier.map((i) =>
        converted[i] ? 0 : Math.max(0, rounds[i].investmentAmount * rounds[i].liquidationPreference)
      );
      const totalClaim = claims.reduce((sum, c) => sum + c, 0);
      if (totalClaim <= EPSILON) continue;

      const payable = Math.min(remaining, totalClaim);
      for (let k = 0; k < tier.length; k++) {
        pref[tier[k]] = (payable * claims[k]) / totalClaim;
      }
      remaining -= payable;
    }

    // Everyone entitled to the residual: common, converted rounds (as common) and
    // participating rounds. Non-participating rounds that kept their preference
    // take no further share.
    const shares = (i: number) => Math.max(0, rounds[i].shares);
    const entitled = rounds.map((r, i) => converted[i] || r.participating);
    const capped = new Array<boolean>(n).fill(false);

    let pool = remaining;
    let perShare = 0;
    let activeShares = 0;

    // Redistribute as each cap binds, until no further cap is breached.
    for (let pass = 0; pass <= n + 1; pass++) {
      activeShares = commonHeld;
      for (let i = 0; i < n; i++) {
        if (entitled[i] && !capped[i]) activeShares += shares(i);
      }
      if (activeShares <= 0 || pool <= EPSILON) {
        perShare = 0;
        break;
      }
      perShare = pool / activeShares;

      let breached = -1;
      for (let i = 0; i < n; i++) {
        // Converted rounds hold common stock, which is never capped.
        if (!entitled[i] || capped[i] || converted[i]) continue;
        const cap = rounds[i].participationCap;
        if (cap === null) continue;
        const allowed = Math.max(0, rounds[i].investmentAmount * cap - pref[i]);
        if (shares(i) * perShare > allowed + EPSILON) {
          breached = i;
          break;
        }
      }
      if (breached === -1) break;

      const allowed = Math.max(
        0,
        rounds[breached].investmentAmount * (rounds[breached].participationCap as number) -
          pref[breached]
      );
      participation[breached] = allowed;
      capped[breached] = true;
      pool -= allowed;
    }

    if (activeShares > 0) {
      for (let i = 0; i < n; i++) {
        if (!entitled[i] || capped[i]) continue;
        const amount = shares(i) * perShare;
        if (converted[i]) common[i] = amount;
        else participation[i] = amount;
      }
      return { pref, participation, common, commonPayout: commonHeld * perShare };
    }

    // Degenerate case: no common is outstanding and every round is either capped
    // or not entitled to the residual, so nobody is left who can take it without
    // breaching a cap. The previous version spread the remainder across *every*
    // round's shares regardless of whether that round was capped, which handed a
    // round capped at 1.5x the whole exit at 6.67x. The caps can be honoured here,
    // so they are; the orphaned remainder is reported against common, the residual
    // claimant, even though no common is outstanding to receive it. That keeps the
    // distribution summing to the exit value while leaving every cap intact.
    return { pref, participation, common, commonPayout: Math.max(0, pool) };
  };

  const totalFor = (d: Distribution, i: number) => d.pref[i] + d.participation[i] + d.common[i];

  // Solve the conversion set. Each pass lets one round switch to whichever choice
  // pays it more; it settles once no round wants to move. Every round is a
  // candidate, participating or not — see the note on the conversion election
  // above.
  //
  // Best-response dynamics like this can cycle, so states already visited are
  // never re-entered and the search is bounded either way.
  const stateKey = (c: boolean[]) => c.map((v) => (v ? '1' : '0')).join('');
  let converted = new Array<boolean>(n).fill(false);
  const seen = new Set<string>([stateKey(converted)]);

  for (let iter = 0; iter < 4 * n + 8; iter++) {
    const base = distribute(converted);
    let changed = false;
    for (let i = 0; i < n; i++) {
      const trial = converted.slice();
      trial[i] = !trial[i];
      if (seen.has(stateKey(trial))) continue;
      if (totalFor(distribute(trial), i) > totalFor(base, i) + EPSILON) {
        converted = trial;
        seen.add(stateKey(trial));
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }

  const final = distribute(converted);

  const results: WaterfallResult[] = rounds.map((round, i) => {
    const total = totalFor(final, i);
    const invested = round.investmentAmount;
    return {
      roundName: round.name,
      liquidationPref: final.pref[i],
      participating: final.participation[i],
      common: final.common[i],
      total,
      roi: invested > 0 ? (total / invested - 1) * 100 : 0,
      multiple: invested > 0 ? total / invested : 0,
    };
  });

  return { results, commonPayout: final.commonPayout };
}
